import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../config/api";
import {
    Card,
    Space,
    Row,
    Col,
    Typography,
    Divider,
    List,
    Spin,
    Alert,
    Tag
} from "antd";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { formatPolishCurrency } from "../../helpers/currency";
import { formatPolishDate } from "../../helpers/date";
import { getAuthToken } from "../../helpers/account";
import {
    ApiTwoTone
} from "@ant-design/icons";
import NewTransactionForm from "../Transaction/Forms/NewTransactionForm";
import AvatarDisplay from "../Dashboard/Account/AvatarDisplay";

const { Title, Text } = Typography;

const InquiryResponsesList = ({ inquiry }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [responses, setResponses] = useState([]);
    const { inquiryUuid: routeInquiryUuid } = useParams();

    const finalInquiryUuid = (inquiry && inquiry.uuid) || routeInquiryUuid;

    // Redux state for listening to inquiry response events
    const inquiryResponseCreated = useSelector(
        (state) => state.inquiryResponseCreated
    );
    const inquiryResponseDeleted = useSelector(
        (state) => state.inquiryResponseDeleted
    );

    const fetchResponses = () => {
        if (!finalInquiryUuid) return;

        const apiUrl = `${api.host}/inquiries/${finalInquiryUuid}/responses`;

        setLoading(true);
        fetch(apiUrl, {
            method: "GET",
            headers: new Headers({
                "Content-Type": "application/json",
                Authorization: `${getAuthToken()}`,
            }),

        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to fetch inquiry responses");
                }
                return response.json();
            })
            .then((data) => {
                setResponses(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching responses:", error);
                setLoading(false);
            });
    };

    // Fetch responses on mount and when actions are dispatched
    useEffect(() => {
        fetchResponses();
    }, [finalInquiryUuid, inquiryResponseCreated, inquiryResponseDeleted]);

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <Row>
                <Col span={24}>
                    <Space direction="vertical" style={{ width: "100%" }}>

                        <div style={{ marginBottom: "24px" }}>
                            <Title level={4} style={{ margin: 0, color: "#2c3e50", marginBottom: "8px" }}>
                                💬 {t('inquiry.responses.title')}
                            </Title>
                            <Alert 
                                type="info"
                                message={t('inquiry.responses.contractInfo')}
                                style={{ borderRadius: "8px" }}
                            />
                        </div>

                        <Spin spinning={loading}>
                            {responses.length > 0 ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                    {responses.map((response) => (
                                        <Card
                                            key={response.uuid}
                                            style={{
                                                border: "none",
                                                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                                borderRadius: "12px",
                                            }}
                                            bodyStyle={{ padding: "24px" }}
                                        >
                                            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                                {/* Freelancer Info */}
                                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                    <AvatarDisplay src={response.account.photo} size={48} />
                                                    <div>
                                                        <Text strong style={{ fontSize: "16px", color: "#2c3e50" }}>
                                                            {response.account.fullname}
                                                        </Text>
                                                        {response.account.company_name && (
                                                            <div style={{ marginTop: "4px" }}>
                                                                <Tag color="blue" style={{ borderRadius: "12px" }}>
                                                                    {response.account.company_name}
                                                                </Tag>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Response Message */}
                                                <div style={{ 
                                                    background: "#f8f9fa", 
                                                    padding: "16px", 
                                                    borderRadius: "8px",
                                                    border: "1px solid #e9ecef"
                                                }}>
                                                    <Text style={{ lineHeight: "1.6" }}>{response.message}</Text>
                                                </div>

                                                {/* Details */}
                                                <div style={{ 
                                                    display: "grid", 
                                                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                                                    gap: "16px",
                                                    padding: "16px",
                                                    background: "#f8f9fa",
                                                    borderRadius: "8px"
                                                }}>
                                                    <div>
                                                        <Text style={{ color: "#6c757d", fontSize: "14px" }}>{t('inquiry.responses.deliveryTime')}</Text>
                                                        <div style={{ marginTop: "4px" }}>
                                                            <Text strong style={{ color: "#2c3e50" }}>
                                                                {response.delivery_days} {t('inquiry.page.days')}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Text style={{ color: "#6c757d", fontSize: "14px" }}>{t('inquiry.responses.cardValue')}</Text>
                                                        <div style={{ marginTop: "4px" }}>
                                                            <Text strong style={{ color: "#667eea", fontSize: "16px" }}>
                                                                {formatPolishCurrency(response.price)}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Text style={{ color: "#6c757d", fontSize: "14px" }}>{t('inquiry.responses.submissionDate')}</Text>
                                                        <div style={{ marginTop: "4px" }}>
                                                            <Text strong style={{ color: "#2c3e50" }}>
                                                                {formatPolishDate(response.created_at)}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Action Button */}
                                                <div style={{ paddingTop: "8px" }}>
                                                    <NewTransactionForm
                                                        initiatorTransactionRoleId={{
                                                            value: 2,
                                                            label: t('profile.buyer'),
                                                        }}
                                                        title={inquiry.name}
                                                        email={response.account.email}
                                                    />
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <Card
                                    style={{
                                        border: "none",
                                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                        borderRadius: "12px",
                                        textAlign: "center"
                                    }}
                                    bodyStyle={{ padding: "40px 24px" }}
                                >
                                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>💬</div>
                                    <Title level={4} style={{ color: "#6c757d", marginBottom: "8px" }}>
                                        {t('inquiry.responses.noResponses')}
                                    </Title>
                                    <Text style={{ color: "#6c757d" }}>
                                        {t('inquiry.responses.noResponsesDesc')}
                                    </Text>
                                </Card>
                            )}
                        </Spin>

                    </Space>
                </Col>
            </Row>
        </Space>
    );
};

export default InquiryResponsesList;
